import { useState, useMemo, useCallback } from "react";

interface UseCompanyNameHandlingProps {
  initialCompanies?: string[];
  onChange?: (companies: string[]) => void;
}

interface IUseCompanyNameHandling {
  companies: string[];
  companiesText: string;
  setCompaniesText: (text: string) => void;
  parseCompanies: (text: string) => string[];
  removeCompany: (index: number) => void;
  clearCompanies: () => void;
  companyCount: number;
  updateCompanies: (newCompanies: string[]) => void;
}

const useCompanyNameHandling = ({
  initialCompanies = [],
  onChange,
}: UseCompanyNameHandlingProps = {}): IUseCompanyNameHandling => {
  const [internalCompanies, setInternalCompanies] =
    useState<string[]>(initialCompanies);

  const [modalText, setModalText] = useState<string>(
    initialCompanies.join("\n"),
  );

  const companiesText = useMemo(() => modalText, [modalText]);

  const parseCompanies = useCallback((text: string): string[] => {
    let companies = text.split("\n");

    companies = companies.flatMap((company) =>
      company.includes(",") ? company.split(",") : company,
    );

    return companies
      .map((company) => company.trim())
      .filter(Boolean)
      .filter((company, index, self) => self.indexOf(company) === index);
  }, []);

  const setCompaniesText = useCallback((text: string) => {
    setModalText(text);
  }, []);

  const removeCompany = useCallback(
    (index: number) => {
      if (index < 0 || index >= internalCompanies.length) return;

      const newCompanies = [...internalCompanies];
      newCompanies.splice(index, 1);
      setInternalCompanies(newCompanies);

      setModalText(newCompanies.join("\n"));

      if (onChange) {
        onChange(newCompanies);
      }
    },
    [internalCompanies, onChange],
  );

  // Clear all companies
  const clearCompanies = useCallback(() => {
    setInternalCompanies([]);
    setModalText("");

    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Update companies from external source
  const updateCompanies = useCallback(
    (newCompanies: string[] | { label: string }[]) => {
      let stringCompanies: string[];

      if (newCompanies.length === 0) {
        stringCompanies = [];
      } else if (typeof newCompanies[0] === "string") {
        stringCompanies = newCompanies as string[];
      } else {
        stringCompanies = (newCompanies as { label: string }[]).map(
          (company) => company.label,
        );
      }

      setInternalCompanies(stringCompanies);
      setModalText(stringCompanies.join("\n"));
    },
    [],
  );

  const companyCount = useMemo(
    () => internalCompanies.length,
    [internalCompanies],
  );

  const companies = useMemo(() => {
    return internalCompanies;
  }, [internalCompanies]);

  return {
    companies,
    companiesText,
    setCompaniesText,
    parseCompanies,
    removeCompany,
    clearCompanies,
    companyCount,
    updateCompanies,
  };
};

export default useCompanyNameHandling;
